import { Link } from "@amzn/stencil-react-components/link";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { View } from "@amzn/stencil-react-components/layout";
import React, { Fragment, ReactNode, useEffect, useState } from "react";

import {
  clearMidwaySessionReloadPending,
  isMidwaySessionReloadPending,
  isMidwaySessionUnavailable,
} from "@/common/helpers/midway-session-error.helper";
import { UserPermissions } from "@/services/user-permissions/user-permissions";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { AEA_REFRESH_GUIDE_URL } from "@/common/constants";

interface AuthLoadingProps {
  children: ReactNode;
  /** Rendered while authorization is pending. */
  loadingSkeleton: ReactNode;
}

type AuthState = "loading" | "success" | "error" | "midway-session-unavailable";

const renderMidwaySessionWarning = (): React.ReactElement => (
  <View padding="2rem">
    <MessageBanner
      type={MessageBannerType.Warning}
      titleText={getString("errors.midwaySessionUnavailableTitle")}
      isDismissible={false}
    >
      {getString("errors.midwaySessionUnavailableDescription")}{" "}
      <Link href={AEA_REFRESH_GUIDE_URL} target="_blank">
        {getString("errors.midwaySessionUnavailableTicket")}
      </Link>
    </MessageBanner>
  </View>
);

/**
 * Runs UserPermissions.authorize() before the app renders, so permissions are
 * available to SessionProvider and Midway auth is triggered. It only resolves
 * authentication; access control lives downstream in each route's PermissionGate.
 * Renders `loadingSkeleton` while loading and an error message on failure.
 */
const AuthLoading: React.FC<AuthLoadingProps> = ({ children, loadingSkeleton }) => {
  const [state, setState] = useState<AuthState>("loading");

  useEffect(() => {
    UserPermissions.current
      .authorize()
      .then(() => {
        // authorize() succeeding means the session recovered (or was never broken),
        // so children will render and any subsequent query failure gets its own
        // fresh reload rather than being conflated with a resolved past failure.
        clearMidwaySessionReloadPending();
        setState("success");
      })
      .catch((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : "Authorization failed";
        console.error("Authentication error:", errorMessage);
        setState(isMidwaySessionUnavailable(error) ? "midway-session-unavailable" : "error");
      });
  }, []);

  // A query/mutation already reloaded once for a Midway session failure. Show the
  // warning immediately on this mount instead of waiting for authorize() to run --
  // this is what caps the recovery flow at exactly one reload per failure episode:
  // if authorize() were to succeed (stale/racing token) while a query still fails,
  // relying on authorize() alone would let the query trigger a second reload.
  if (state === "loading" && isMidwaySessionReloadPending()) {
    return renderMidwaySessionWarning();
  }

  if (state === "loading") {
    return <Fragment>{loadingSkeleton}</Fragment>;
  }

  if (state === "midway-session-unavailable") {
    return renderMidwaySessionWarning();
  }

  if (state === "error") {
    return (
      <View padding="2rem">
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {getString("errors.authenticationFailed")}
        </MessageBanner>
      </View>
    );
  }

  return <Fragment>{children}</Fragment>;
};

export default AuthLoading;
